import { useState, useCallback } from 'react';
import { useNotifications, useRowContextMenu } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';

/** Single-consultation actions from a row: regenerate, delete, and the context menu offering them. */
const useScribeRowActions = ({ consultationService, hasConsultWrite }) => {
  const { addNotification } = useNotifications();
  const { menu: rowContextMenu, close: closeRowContextMenu, handleRowContextMenu: openRowContextMenu } = useRowContextMenu();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [consultationToDelete, setConsultationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [consultationToRegenerate, setConsultationToRegenerate] = useState(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);

  // Shared by the Actions column and the right-click context menu so they stay in sync.
  const getRowActions = useCallback((consultation) => {
    if (!hasConsultWrite || !consultation) {
      return { canRegenerate: false, canDelete: false };
    }
    const status = consultation.status;
    return {
      canRegenerate: status === ConsultationStatusEnum.READY.value,
      canDelete: status === ConsultationStatusEnum.READY.value ||
        status === ConsultationStatusEnum.INVALID.value ||
        status === ConsultationStatusEnum.ABANDONED.value,
    };
  }, [hasConsultWrite]);

  const handleRowContextMenu = useCallback((row, index, event) => {
    const actions = getRowActions(row);
    if (!actions.canRegenerate && !actions.canDelete) return;
    openRowContextMenu(row, index, event);
  }, [getRowActions, openRowContextMenu]);

  const handleDeleteClick = useCallback((consultation) => {
    setConsultationToDelete(consultation);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setConsultationToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!consultationToDelete) return;

    try {
      setDeleteLoading(true);
      await consultationService.deleteConsultation(consultationToDelete.consultationId);
      setDeleteModalOpen(false);
      setConsultationToDelete(null);
      addNotification('scribe-delete-success', 'Consultation deleted');
    } catch (error) {
      console.error('Error deleting consultation:', error);
      addNotification('scribe-delete-error', 'Failed to delete consultation', { isError: true });
    } finally {
      setDeleteLoading(false);
    }
  }, [consultationToDelete, consultationService, addNotification]);

  const handleRegenerateClick = useCallback((consultation) => {
    setConsultationToRegenerate(consultation);
    setRegenerateModalOpen(true);
  }, []);

  const closeRegenerateModal = useCallback(() => {
    setRegenerateModalOpen(false);
    setConsultationToRegenerate(null);
  }, []);

  const confirmRegenerate = useCallback(() => {
    if (!consultationToRegenerate) return;

    setRegenerateLoading(true);
    consultationService.regenerateConsultation(consultationToRegenerate.consultationId)
      .then(() => {
        addNotification(`regenerate-success-${consultationToRegenerate.consultationId}`, 'Consultation queued for regeneration');
        setRegenerateModalOpen(false);
        setConsultationToRegenerate(null);
      })
      .catch(error => {
        console.error('Error regenerating consultation:', error);
        addNotification(`regenerate-error-${consultationToRegenerate.consultationId}`, 'Failed to regenerate consultation', { isError: true });
      })
      .finally(() => {
        setRegenerateLoading(false);
        setRegenerateModalOpen(false);
        setConsultationToRegenerate(null);
      });
  }, [consultationToRegenerate, consultationService, addNotification]);

  return {
    getRowActions,
    rowContextMenu,
    closeRowContextMenu,
    handleRowContextMenu,
    deleteModalOpen,
    consultationToDelete,
    deleteLoading,
    handleDeleteClick,
    closeDeleteModal,
    confirmDelete,
    regenerateModalOpen,
    consultationToRegenerate,
    regenerateLoading,
    handleRegenerateClick,
    closeRegenerateModal,
    confirmRegenerate,
  };
};

export default useScribeRowActions;
